/**
 * Company delete API client (SF-4324, epic SF-4321; scheduled deletion SF-4813).
 *
 * Wraps the sf-api admin company-delete endpoints so the company page's
 * "Danger zone" and the Deleted companies tab share one set of paths and
 * response types. Mirrors the data-access pattern in src/pages/eva/api/evaBudgetClient.ts.
 *
 *   GET    /admin/v1/companies/:id/delete-impact     impact counts + graceDays + open deletion
 *   DELETE /admin/v1/companies/:id                   body { confirmName } schedules the deletion
 *   POST   /admin/v1/companies/:id/deletion/cancel   only while scheduled
 *   POST   /admin/v1/companies/:id/deletion/retry    only when failed or stopped (202)
 *   POST   /admin/v1/companies/:id/deletion/force    SF-5057: delete a waiting deletion now (202)
 *   GET    /admin/v1/companies/deletions?view=active|history&page&limit
 * All need admin:companies:delete (force also needs admin:companies:delete:force). Error bodies are { success: false, message, code }.
 *
 * ⚠️ sf-api wraps every response in `{ success, data }`. The Refine
 * `dataProvider.custom` unwraps the sf-api envelope and returns `{ data:
 * innerPayload }`, so `useCustom` exposes the inner payload via
 * `query.data?.data` (same convention as the Eva hooks).
 */
import { useCustom, useCustomMutation } from '@refinedev/core';
import type { AxiosError } from 'axios';
import type { AccountType } from '../constants';

/** Base path for the admin companies endpoints on sf-api. */
export const COMPANIES_API_BASE = 'admin/v1/companies';

/** Permission gating both delete endpoints (matches sf-api requirePermissions). */
export const COMPANY_DELETE_PERMISSION = 'admin:companies:delete';

/** SF-5057: also needed to skip the waiting period (matches sf-api requirePermissions). */
export const COMPANY_DELETE_FORCE_PERMISSION = 'admin:companies:delete:force';

/** One per-collection would-delete count (only nonzero entries are returned). */
export interface DeleteImpactCollection {
  model: string;
  count: number;
}

/**
 * Impact summary returned by GET :id/delete-impact (mirrors sf-api
 * companyPurgeService.getCompanyDeleteImpact + the controller's additions).
 * `companyName` drives the type-the-name confirmation; `manual_steps` lists
 * cleanup the purge does NOT automate (Chargebee).
 */
export interface CompanyDeleteImpact {
  company_id: string;
  collections: DeleteImpactCollection[];
  total: number;
  manual_steps: string[];
  companyName: string;
  deletionStatus: string | null;
  /** SF-4813: days between the request and the deletion (missing on an older sf-api). */
  graceDays?: number;
  /** SF-4813: the company's open deletion, or null. */
  deletion?: CompanyDeletionRow | null;
  /** SF-5158: whether the company is active (missing on an older sf-api). */
  isActive?: boolean;
}

/** Who asked for (or cancelled) a deletion. Null-safe: the person may be gone. */
export interface DeletionActor {
  name?: string | null;
  email?: string | null;
}

/** Every state of a deletion record (sf-api CompanyDeletion.status). */
export type CompanyDeletionStatus =
  | 'scheduled'
  | 'cancelled'
  | 'running'
  | 'failed'
  | 'stopped'
  | 'done';

/** One deletion record as sf-api returns it (brief S4.4). */
export interface CompanyDeletionRow {
  _id: string;
  company_id: string;
  company_name?: string | null;
  status: CompanyDeletionStatus;
  requested_by?: DeletionActor | null;
  requested_at?: string | null;
  delete_after?: string | null;
  attempts?: number;
  last_attempt_at?: string | null;
  finished_at?: string | null;
  cancelled_at?: string | null;
  cancelled_by?: DeletionActor | null;
  result?: { total_deleted?: number; by_model?: { model: string; count: number }[] } | null;
  /** The brief allows either name (Mongoose reserves `errors`); read both. */
  errors?: string[] | null;
  error_messages?: string[] | null;
  /** SF-5139: what the purge could not do itself and a person must (latest attempt). */
  manual_steps?: string[] | null;
  can_cancel?: boolean;
  can_retry?: boolean;
  /** Failed or stopped with attempts left: the nightly job tries again. */
  auto_retry_pending?: boolean;
  /**
   * SF-5057 fields, missing on an older sf-api. `can_force`: the deletion is waiting, the
   * company is Demo or Test and was created under 30 days ago (the person's permission and
   * company list are checked separately). A null account type reads as Paid.
   */
  forced_at?: string | null;
  forced_by?: DeletionActor | null;
  company_account_type?: AccountType | null;
  company_created_at?: string | null;
  can_force?: boolean;
}

/** One page of GET /deletions. */
export interface CompanyDeletionsPage {
  rows: CompanyDeletionRow[];
  total: number;
  page: number;
  limit: number;
  /**
   * Active view only: deletions on ANY page that are running or forced and waiting to start.
   * Missing on an older sf-api.
   */
  in_flight?: number;
}

export type CompanyDeletionsView = 'active' | 'history';

/** Rows per page on the Deleted companies tab. */
export const DELETIONS_PAGE_SIZE = 20;

/** The nightly job tries a failed deletion this many times in total (sf-api constant). */
export const MAX_AUTO_ATTEMPTS = 3;

/**
 * DELETE answer: the new deletion row (SF-4812). An older sf-api purged at once, but the
 * dialog never sends to one: it needs `graceDays` from delete-impact first.
 */
export type CompanyDeleteAccepted = CompanyDeletionRow;

/**
 * SF-5158: sf-api refuses to schedule the deletion of an active company (409, this code).
 * The same words grey the danger zone's Delete button while the company is active.
 */
export const COMPANY_STILL_ACTIVE_CODE = 'COMPANY_STILL_ACTIVE';
export const COMPANY_STILL_ACTIVE_MESSAGE = 'Mark the company inactive first';

/** True when sf-api refused a deletion because the company is still active. */
export const isStillActiveError = (error: unknown): boolean =>
  (error as AxiosError<{ code?: string }> | undefined)?.response?.data?.code ===
  COMPANY_STILL_ACTIVE_CODE;

/** Generic Schedule failure copy (5xx and anything unexpected). */
export const SCHEDULE_FAILED_MESSAGE = 'Could not schedule the deletion. Please try again.';

/**
 * Fetch the would-delete impact summary for a company. Disabled until the
 * modal opens (`enabled`) so the counts are fetched live at confirmation time,
 * not on page load. Errors are surfaced inline in the modal, so the global
 * error toast is suppressed.
 * @param companyId - company ObjectId
 */
export function useCompanyDeleteImpact(companyId: string, options: { enabled?: boolean } = {}) {
  return useCustom<CompanyDeleteImpact>({
    url: `/${COMPANIES_API_BASE}/${companyId}/delete-impact`,
    method: 'get',
    queryOptions: {
      enabled: (options.enabled ?? true) && !!companyId,
      // The modal fetches on open; a focus refetch mid-confirmation adds no
      // value and would re-render the impact list under the operator.
      refetchOnWindowFocus: false,
    },
    errorNotification: false,
  });
}

/** Mutation to schedule a company's deletion (confirm-name DELETE). */
export function useDeleteCompany() {
  return useCustomMutation<CompanyDeleteAccepted>();
}

/**
 * Build the DELETE mutation args. Extracted so the component and tests share
 * one source of truth for the URL + method + body shape.
 * @param companyId - company ObjectId
 * @param confirmName - must exactly match the company name (server-enforced)
 */
export function deleteCompanyMutationArgs(companyId: string, confirmName: string) {
  return {
    url: `/${COMPANIES_API_BASE}/${companyId}`,
    method: 'delete' as const,
    values: { confirmName },
    // Failures (400 mismatch / 403 / 409 already scheduled) are surfaced inline in
    // the confirmation modal with distinct copy; no global toast.
    errorNotification: false as const,
    successNotification: false as const,
  };
}

/** "SurveyResponse" → "Survey Response" for the modal's impact list. */
export function humanizeModelName(model: string): string {
  return model.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/^./, (c) => c.toUpperCase());
}

/**
 * Map a DELETE failure to distinct, readable copy (400/403/409, SF-4324 AC6). A 5xx (or no
 * reply) never shows the server's own text, which may be a raw exception.
 */
export function deleteErrorMessage(error: unknown): string {
  const axiosErr = error as AxiosError<{ message?: string }> | undefined;
  const status = axiosErr?.response?.status;
  if (!status || status >= 500) return SCHEDULE_FAILED_MESSAGE;
  const serverMessage = axiosErr?.response?.data?.message;
  if (status === 400) {
    return serverMessage ?? 'The name you typed does not match the company name.';
  }
  if (status === 403) {
    return 'You do not have permission to delete companies.';
  }
  if (status === 409) {
    if (isStillActiveError(error)) return serverMessage ?? COMPANY_STILL_ACTIVE_MESSAGE;
    return serverMessage ?? 'This company already has a deletion scheduled or running.';
  }
  return serverMessage ?? SCHEDULE_FAILED_MESSAGE;
}

/** Query key root of the Deleted companies tab, so an action can refresh both tables. */
export const companyDeletionsQueryKey = ['sf-admin', 'company-deletions'];

/**
 * One page of deletions. `active` = scheduled, running, failed, stopped (soonest first);
 * `history` = done and cancelled (newest first). Server-paginated.
 */
export function useCompanyDeletions(
  view: CompanyDeletionsView,
  page: number,
  options: { enabled?: boolean; refetchInterval?: number | false } = {},
) {
  return useCustom<CompanyDeletionsPage>({
    url: `/${COMPANIES_API_BASE}/deletions`,
    method: 'get',
    config: { query: { view, page, limit: DELETIONS_PAGE_SIZE } },
    queryOptions: {
      queryKey: [...companyDeletionsQueryKey, view, page],
      enabled: options.enabled ?? true,
      // Each table shows its own errors (and a Retry), so no global toast.
      retry: false,
      refetchInterval: options.refetchInterval ?? false,
    },
    errorNotification: false,
  });
}

/**
 * How often the Deleted companies tab reads both tables again while a deletion is under way,
 * so staff see it finish without reloading. 30 seconds (Alan's ask): a forced purge takes from
 * a few seconds to about 40 on stage, and the server runs at most 3 at once.
 */
export const DELETIONS_POLL_MS = 30_000;

/**
 * True while a deletion is still moving on its own: running, or forced and waiting for the
 * server to start it (a forced deletion stays `scheduled` until one of the 3 slots frees).
 * Ordinary scheduled rows wait for the nightly job; failed and stopped rows wait for a manual
 * Retry or an eligible nightly retry. Neither needs polling while it waits.
 *
 * Uses sf-api's `in_flight` count, which covers every page: a force moves its row to an
 * earlier page (it becomes due now), so the page on screen alone can miss it. The rows on
 * screen still count too: an older sf-api sends no count, and see the race below.
 */
export const deletionsInFlight = (page: CompanyDeletionsPage | undefined): boolean => {
  if (!page) return false;
  if (typeof page.in_flight === 'number' && page.in_flight > 0) return true;
  // Also the rows on screen, even when the count says 0: sf-api reads the rows and the count
  // separately, so a deletion that ends between the two reads comes back as a running row with
  // a count of 0. Reading again then clears that row instead of freezing it as Deleting.
  return page.rows.some(
    (r) => r.status === 'running' || (r.status === 'scheduled' && !!r.forced_at),
  );
};

/** Mutation used for Cancel and Retry. */
export function useDeletionAction() {
  return useCustomMutation<CompanyDeletionRow>();
}

const deletionActionArgs = (companyId: string, action: 'cancel' | 'retry') => ({
  url: `/${COMPANIES_API_BASE}/${encodeURIComponent(companyId)}/deletion/${action}`,
  method: 'post' as const,
  values: {},
  errorNotification: false as const,
  successNotification: false as const,
});

/** Cancel a scheduled deletion: the company goes back to how it was. */
export const cancelDeletionArgs = (companyId: string) => deletionActionArgs(companyId, 'cancel');

/** Start a failed or stopped deletion again, now (202, runs in the background). */
export const retryDeletionArgs = (companyId: string) => deletionActionArgs(companyId, 'retry');

/** SF-5057: delete a waiting Demo or Test company now instead of after the waiting period. */
export const forceDeletionUrl = (companyId: string) =>
  `/${COMPANIES_API_BASE}/${encodeURIComponent(companyId)}/deletion/force`;

/** What a cancel does, for its confirmation and its success message. */
export const CANCEL_DONE_MESSAGE =
  'The deletion is cancelled and the company goes back to how it was.';

/** Success message after Cancel or Retry. */
export const deletionActionDoneMessage = (action: 'cancel' | 'retry') =>
  action === 'cancel' ? CANCEL_DONE_MESSAGE : 'Deletion started again.';

/** Cancel/Retry failure copy: the server's reason on a 409, plain text otherwise. */
export function deletionActionErrorMessage(error: unknown, action: 'cancel' | 'retry'): string {
  const axiosErr = error as AxiosError<{ message?: string }> | undefined;
  const status = axiosErr?.response?.status;
  if (status === 409 && axiosErr?.response?.data?.message) return axiosErr.response.data.message;
  if (status === 403) return 'You do not have permission to change this company.';
  return action === 'cancel'
    ? 'Could not cancel the deletion. Please try again.'
    : 'Could not retry the deletion. Please try again.';
}

/** The record's error strings, whichever name sf-api used. */
export const deletionErrors = (row: Pick<CompanyDeletionRow, 'errors' | 'error_messages'>) =>
  row.error_messages ?? row.errors ?? [];

/** "20 Oct 2026" style date for deletion screens; empty when missing. */
export function formatDeletionDate(iso?: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/** Whole days from now until `iso` (rounded up); 0 or less means it is due. */
export function daysUntil(iso: string, now: number = Date.now()): number {
  return Math.ceil((new Date(iso).getTime() - now) / 86_400_000);
}

/** "in 3 days" / "in 1 day" / "due at the next nightly run". */
export function daysLeftLabel(iso: string, now: number = Date.now()): string {
  const days = daysUntil(iso, now);
  if (days <= 0) return 'due at the next nightly run';
  return `in ${days} day${days === 1 ? '' : 's'}`;
}

/** "2 of 3"; a manual retry can go past the automatic limit, then just the number. */
export function attemptsLabel(attempts = 0): string {
  return attempts <= MAX_AUTO_ATTEMPTS ? `${attempts} of ${MAX_AUTO_ATTEMPTS}` : String(attempts);
}

/** True when the DELETE answer is a deletion row (SF-4812), not the old purge answer. */
export const isDeletionRow = (value: unknown): value is CompanyDeletionRow =>
  !!value && typeof value === 'object' && 'status' in value && '_id' in value;
